// Voicemail drop on a live call (round 13, "drop and go"): when it can be used and why not, what the call's record
// says about a drop, the dock's one-second "Dropping" card, and the ledger's words for a dropped call. Pure apart
// from the small tab-local store at the end, which remembers drops this tab started until the server's record of
// the call says the same (briefs/B11.md "Design (pinned)").
//
// The agent decides when the beep has passed: nothing listens for it (UX spec 6.9). The server queues the command,
// releases the agent, then attempts playback. Release and playback results are independent observations.
import type { Call } from '@bcio-voice/contracts';
import type { BridgeCall } from '../../call/bridge.ts';
import { useSyncExternalStore } from 'react';
import { formatDuration } from '../../lib/format.ts';

export const DROP_ACTION = 'voicemail_drop';
/** Cosmetic duration after confirmed release; never a release/readiness deadline. */
export const DROPPING_CARD_MS = 1000;
export const DROP_OUTCOMES = ['voicemail_dropped', 'voicemail_drop_interrupted'] as const;

/** Emergency and test dialing: the contract's EmergencyDialSchema and the controller's own list. Never a drop. */
const EMERGENCY = /^(?:\+?1)?9?(?:911|933|112|999|000|110|119)$/;

// ---- The call's record ---------------------------------------------------------------------------------

export type DropPhase = 'pending' | 'playing' | 'completed' | 'interrupted' | 'failed';

/** `call.voicemail_drop` as the web reads it: {drop_id, name, started_at, ended_at, played_ms, result}. */
export interface CallDrop {
  dropId: string | null;
  name: string | null;
  startedAt: string | null;
  endedAt: string | null;
  playedMs: number | null;
  /** The drop's full length, when the record or this tab knows it. */
  durationMs: number | null;
  phase: DropPhase;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
const text = (value: unknown, max: number): string | null => {
  if (typeof value !== 'string') return null;
  const t = value.replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/\s+/g, ' ').trim();
  return t ? t.slice(0, max) : null;
};
const ms = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.round(value) : null);
const when = (value: unknown): string | null => (typeof value === 'string' && Number.isFinite(Date.parse(value)) ? value : null);

const RESULT: Record<string, DropPhase> = {
  playing: 'playing', dropping: 'pending', started: 'pending', in_progress: 'pending',
  completed: 'completed', complete: 'completed', dropped: 'completed', voicemail_dropped: 'completed',
  interrupted: 'interrupted', voicemail_drop_interrupted: 'interrupted', remote_hangup: 'interrupted',
  failed: 'failed', error: 'failed',
};

/** The outcome field, read as a plain string so new codes don't need a contract change here. */
export const outcomeCode = (call: Pick<Call, 'outcome'>): string | null => (call.outcome as string | null) ?? null;

/**
 * What the call's record says about a drop, or null when it has none. Reads `voicemail_drop` and, for a record
 * without it, the `dropping` state or the drop outcomes alone. `dropping` is written before release/playback,
 * so it remains pending. Missing or unknown results never become completed by inference.
 */
export function readCallDrop(call: Call): CallDrop | null {
  const raw: unknown = (call as { voicemail_drop?: unknown }).voicemail_drop;
  const outcome = outcomeCode(call);
  if (isRecord(raw)) {
    const result = typeof raw.result === 'string' ? RESULT[raw.result] ?? null : null;
    const phase: DropPhase = result
      ?? (outcome === 'voicemail_drop_interrupted' ? 'interrupted' : outcome === 'voicemail_dropped' ? 'completed' : 'pending');
    return {
      dropId: text(raw.drop_id, 128), name: text(raw.name, 100), startedAt: when(raw.started_at), endedAt: when(raw.ended_at),
      playedMs: ms(raw.played_ms), durationMs: ms(raw.duration_ms), phase,
    };
  }
  if (outcome === 'voicemail_dropped' || outcome === 'voicemail_drop_interrupted') {
    return { dropId: null, name: null, startedAt: null, endedAt: call.ended_at, playedMs: null, durationMs: null, phase: outcome === 'voicemail_dropped' ? 'completed' : 'interrupted' };
  }
  if (call.state === 'dropping') return { dropId: null, name: null, startedAt: null, endedAt: null, playedMs: null, durationMs: null, phase: 'pending' };
  return null;
}

// ---- Drops this tab started (until the server's record says so) -------------------------------------------

/** `started` records server-confirmed agent departure, never acceptance or playback completion. */
export type LocalPhase = 'sending' | 'queued' | 'releasing' | 'started' | 'failed' | 'unknown';

export interface LocalDrop {
  callId: string;
  dropId: string;
  name: string;
  durationMs: number;
  phase: LocalPhase;
  /** When this tab sent the command (the agent's decision time, as this tab saw it). */
  at: number;
  /** Plain words for a refusal or an unconfirmed send. */
  error: string | null;
  requestId: string | null;
  /** Identity and server version of this intent; never treat another user's receipt as our release. */
  userId?: string;
  requestedVersion?: number;
}

/** The drop for this call: the server's record wins; a drop this tab started fills in until it arrives. */
export function effectiveDrop(call: Call, local: LocalDrop | null | undefined): CallDrop | null {
  const server = readCallDrop(call);
  if (server) {
    return local && local.dropId && (!server.dropId || server.dropId === local.dropId)
      ? { ...server, name: server.name ?? local.name, durationMs: server.durationMs ?? local.durationMs }
      : server;
  }
  if (local && local.callId === call.id && local.phase !== 'failed') {
    return { dropId: local.dropId, name: local.name, startedAt: null, endedAt: null, playedMs: null, durationMs: local.durationMs, phase: 'pending' };
  }
  return null;
}

/** An observed local microphone/media path still needs its call card, even after a server release. */
export function hasOpenDropAudio(callId: string, engineCalls: readonly BridgeCall[]): boolean {
  return engineCalls.some(c => c.callId?.toLowerCase() === callId.toLowerCase()
    && (c.microphone !== 'none' || c.media === 'connected' || c.media === 'connecting' || c.media === 'recovering'));
}

/** The controller writes left_at only after releaseDropAgent confirms the channel absent. Neither the
 * dropping state nor an applied command proves that this user's participant left. A local SIP/mic still
 * open vetoes removal from the dock. Event hints are deliberately not used as release receipts. */
export function dropLineReleased(call: Call, local: LocalDrop | null | undefined, userId: string, engineCalls: readonly BridgeCall[] = []): boolean {
  if (!userId || call.direction !== 'outbound' || call.destination.kind !== 'pstn' || hasOpenDropAudio(call.id, engineCalls)) return false;
  const drop = readCallDrop(call);
  if (!drop?.startedAt) return false;
  if (local && (local.callId !== call.id || local.userId !== userId || local.requestedVersion === undefined
    || call.version <= local.requestedVersion || (drop.dropId !== null && drop.dropId !== local.dropId))) return false;
  const mine = call.participants.filter(p => p.kind === 'agent' && p.user_id === userId);
  return mine.length > 0 && mine.every(p => {
    const joined = p.joined_at ? Date.parse(p.joined_at) : NaN;
    const left = p.left_at ? Date.parse(p.left_at) : NaN;
    return Number.isFinite(joined) && Number.isFinite(left) && left >= joined && left >= Date.parse(drop.startedAt!);
  });
}

export function isDropping(call: Call, local: LocalDrop | null | undefined, userId: string, engineCalls: readonly BridgeCall[] = []): boolean {
  return call.state !== 'ended' && call.state !== 'failed' && dropLineReleased(call, local, userId, engineCalls);
}

/** Workspace.liveCalls excludes dropping. Retain unresolved drops (and terminal drops with open local
 * audio) explicitly, using the same candidates for rendering, announcements and keyboard controls. */
export function dropDockCalls(calls: ReadonlyMap<string, Call>, liveCalls: readonly Call[], locals: ReadonlyMap<string, LocalDrop>, userId: string, engineCalls: readonly BridgeCall[]): Call[] {
  const candidates = new Map(liveCalls.map(c => [c.id, c]));
  for (const call of calls.values()) {
    const local = locals.get(call.id);
    const ownIntent = local?.userId === userId;
    const openAudio = hasOpenDropAudio(call.id, engineCalls);
    if (!ownIntent && !openAudio && !call.participants.some(p => p.kind === 'agent' && p.user_id === userId)) continue;
    if (call.state === 'dropping' || (ownIntent && call.state !== 'ended' && call.state !== 'failed')
      || (effectiveDrop(call, local) && openAudio)) candidates.set(call.id, call);
  }
  return [...candidates.values()];
}

/** Any call with a drop on it, live or ended: the Calls page's "Voicemail dropped" filter. */
export function isDroppedCall(call: Call, local: LocalDrop | null | undefined = null): boolean {
  return effectiveDrop(call, local) !== null;
}

// ---- Eligibility -----------------------------------------------------------------------------------------

export type DropBlock = 'already_dropped' | 'inbound' | 'internal' | 'emergency' | 'monitored' | 'not_answered' | 'held' | 'ended' | 'no_control' | 'busy' | 'sending' | 'unconfirmed';

export interface DropEligibility {
  enabled: boolean;
  block: DropBlock | null;
  /** The plain reason beside a disabled control. */
  reason: string | null;
}

export const DROP_BLOCK_TEXT: Record<DropBlock, string> = {
  already_dropped: 'A voicemail drop has already been requested on this call.',
  inbound: 'Voicemail drop is for calls you place. This call came in.',
  internal: 'Voicemail drop works on calls to outside numbers, not on calls with teammates.',
  emergency: 'Voicemail drop is never available on emergency calls.',
  monitored: 'Not available while a coach is on this call.',
  not_answered: 'Available once the call is answered.',
  held: 'Resume the call first. A voicemail can’t be dropped while it’s on hold.',
  ended: 'This call has ended.',
  no_control: 'You can’t control this call from here.',
  busy: 'Wait for the current action to finish.',
  sending: 'Sending the voicemail drop request. Your line’s release is not confirmed.',
  unconfirmed: 'A drop was sent and not confirmed yet. Check again before trying another.',
};

export function isEmergencyCall(call: Pick<Call, 'destination'>): boolean {
  return call.destination.kind === 'pstn' && EMERGENCY.test(call.destination.e164.replace(/[\s()-]/g, ''));
}

/**
 * The options route's `reason_code`, for the refusals that don't depend on the call's state (which this tab reads
 * from the call itself, so a reason read while the call was ringing doesn't outlive the answer).
 */
export function serverBlock(reasonCode: string | null | undefined): DropBlock | null {
  switch (reasonCode) {
    case 'voicemail_drop_already_used':
      return 'already_dropped';
    case 'voicemail_drop_emergency':
      return 'emergency';
    case 'voicemail_drop_monitored':
      return 'monitored';
    default:
      return null;
  }
}

/**
 * Whether the drop control can be used, checked in the order a person would want to hear it. The server applies
 * the same rules to the command (answered outbound PSTN, not held, not emergency, not monitored, one per call);
 * `allowed_actions` stays the authority on control: no actions at all means this person can't control the call.
 */
export function dropEligibility(call: Call, ctx: { local?: LocalDrop | null; pending?: boolean; serverReason?: string | null } = {}): DropEligibility {
  const block = (b: DropBlock): DropEligibility => ({ enabled: false, block: b, reason: DROP_BLOCK_TEXT[b] });
  if (ctx.local?.phase === 'sending') return block('sending');
  // One drop per call: an unanswered send may have started one, so a second waits for Check again.
  if (ctx.local?.phase === 'unknown') return block('unconfirmed');
  if (ctx.local?.phase === 'queued' || ctx.local?.phase === 'releasing') return block('unconfirmed');
  if (effectiveDrop(call, ctx.local)) return block('already_dropped');
  if (call.direction === 'inbound') return block('inbound');
  if (call.direction === 'internal' || call.destination.kind === 'internal') return block('internal');
  if (isEmergencyCall(call)) return block('emergency');
  if (call.coaching?.present) return block('monitored');
  const server = serverBlock(ctx.serverReason);
  if (server) return block(server);
  if (call.state === 'ended' || call.state === 'failed') return block('ended');
  // A manual selection before answer is queued on the server for this call.
  if (call.state === 'held') return block('held');
  if (call.allowed_actions.length === 0 || (!call.allowed_actions.includes(DROP_ACTION) && !call.allowed_actions.includes('hangup'))) return block('no_control');
  if (ctx.pending) return block('busy');
  return { enabled: true, block: null, reason: null };
}

/** The drop button of a call's card (Alt+Shift+V clicks it, so the card's own rules apply). */
export const dropButtonId = (callId: string) => `vmd-drop-${callId}`;

export function dropButton(scope: { getElementById: (id: string) => Element | null }, callId: string): HTMLElement | null {
  const el = scope.getElementById(dropButtonId(callId)) as HTMLElement | null;
  return el && typeof el.click === 'function' ? el : null;
}

// ---- The dock's one-second card ----------------------------------------------------------------------------

export interface DroppingCard {
  callId: string;
  name: string;
  durationMs: number | null;
  /** When the dock first confirmed agent release and local audio closure. */
  at: number;
}

export interface DropTransition {
  /** Cosmetic cards: each for DROPPING_CARD_MS after observed release. */
  cards: DroppingCard[];
  /** Calls whose card has already been shown, so a later update never shows it again. */
  seen: string[];
}

export type DropTransitionEvent =
  | { type: 'started'; callId: string; name: string; durationMs: number | null; at: number }
  | { type: 'tick'; now: number };

export const NO_TRANSITION: DropTransition = { cards: [], seen: [] };

export function dropTransitionReducer(state: DropTransition, event: DropTransitionEvent): DropTransition {
  switch (event.type) {
    case 'started':
      if (state.seen.includes(event.callId)) return state;
      return { cards: [...state.cards, { callId: event.callId, name: event.name, durationMs: event.durationMs, at: event.at }], seen: [...state.seen, event.callId].slice(-50) };
    case 'tick': {
      const cards = state.cards.filter(c => event.now - c.at < DROPPING_CARD_MS);
      return cards.length === state.cards.length ? state : { ...state, cards };
    }
    default:
      return state;
  }
}

/** The milliseconds until the next card leaves, or null when none shows. */
export function nextExpiry(state: DropTransition, now: number): number | null {
  if (state.cards.length === 0) return null;
  return Math.max(0, Math.min(...state.cards.map(c => c.at + DROPPING_CARD_MS - now)));
}

// ---- Words -----------------------------------------------------------------------------------------------

/** Reserve completed-delivery words for an explicit successful result. */
export function dropOutcomeLabel(drop: CallDrop): string {
  if (drop.phase === 'failed') return 'Voicemail drop failed';
  if (drop.phase === 'interrupted') return 'Voicemail drop interrupted';
  return drop.phase === 'completed' ? 'Voicemail dropped' : 'Voicemail drop in progress';
}

/** The ledger row's badge: the drop's name and where it got to (playing, then completed or interrupted). */
export function dropBadge(drop: CallDrop): string {
  const name = drop.name ?? 'Voicemail drop';
  switch (drop.phase) {
    case 'pending':
      return `${name}, playback not confirmed`;
    case 'playing':
      return `${name}, playing`;
    case 'completed':
      return drop.playedMs !== null ? `${name}, completed ${formatDuration(drop.playedMs)}` : `${name}, completed`;
    case 'interrupted':
      // The controller estimates an interrupted drop's played time from elapsed time; it says so, and so do we.
      return drop.playedMs !== null ? `${name}, interrupted at about ${formatDuration(drop.playedMs)}` : `${name}, interrupted`;
    default:
      return `${name}, not played`;
  }
}

/** The detail's "Drop result" fact. */
export function dropResultText(drop: CallDrop): string {
  switch (drop.phase) {
    case 'pending':
      return 'Drop requested; playback is not confirmed';
    case 'playing':
      return 'Playing to the other side now';
    case 'completed':
      return 'Played to the end, then the call ended';
    case 'interrupted':
      return 'The message didn’t play to the end';
    default:
      return 'The phone system couldn’t play it';
  }
}

/** "0:24", "About 0:12 of 0:24" for an interrupted drop (an estimate), or null while nothing is known. */
export function playedText(drop: CallDrop): string | null {
  if (drop.phase === 'playing' || drop.phase === 'pending') return null;
  if (drop.playedMs === null) return drop.durationMs !== null && drop.phase === 'completed' ? formatDuration(drop.durationMs) : null;
  const played = drop.durationMs !== null && drop.durationMs > 0 && drop.playedMs < drop.durationMs ? `${formatDuration(drop.playedMs)} of ${formatDuration(drop.durationMs)}` : formatDuration(drop.playedMs);
  return drop.phase === 'interrupted' ? `About ${played}` : played;
}

// ---- Tab-local store -------------------------------------------------------------------------------------

/** Drops this tab started, by call. The dock, the shortcut and the ledger read the same store. */
export class LocalDropStore {
  #map: ReadonlyMap<string, LocalDrop> = new Map();
  readonly #listeners = new Set<() => void>();
  getSnapshot = (): ReadonlyMap<string, LocalDrop> => this.#map;
  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  };
  get(callId: string): LocalDrop | null {
    return this.#map.get(callId) ?? null;
  }
  set(drop: LocalDrop): void {
    const next = new Map(this.#map);
    next.set(drop.callId, drop);
    this.#map = next;
    for (const l of [...this.#listeners]) l();
  }
  patch(callId: string, patch: Partial<LocalDrop>): void {
    const current = this.#map.get(callId);
    if (current) this.set({ ...current, ...patch });
  }
  clear(callId: string): void {
    if (!this.#map.has(callId)) return;
    const next = new Map(this.#map);
    next.delete(callId);
    this.#map = next;
    for (const l of [...this.#listeners]) l();
  }
}

export const localDrops = new LocalDropStore();

export function useLocalDrops(store: LocalDropStore = localDrops): ReadonlyMap<string, LocalDrop> {
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}
